import React, { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form';
import type { AutosaveState } from '@components/AutosaveStatus';
import { useAutosave } from '@hooks/use-auto-save';
import { Box, Paper } from '@mui/material';

import { useUpdateClaimMutation } from '../../store/api';
import type { Claim } from '../../types';

import { AnnotationsPanelErrorBoundary } from './ClaimScrubber/AnnotationsPanelErrorBoundary';
// Import all box components
import {
  Box01_InsuranceType,
  Box01a_InsuredId,
  Box02_PatientName,
  Box03_PatientBirthSex,
  Box04_InsuredName,
  Box05_PatientAddress,
  Box06_PatientRelationship,
  Box07_InsuredAddress,
  Box08_ReservedNUCC,
  Box09_OtherInsured,
  Box10_PatientCondition,
  Box11_InsuredPolicy,
  Box12_PatientSignature,
  Box13_InsuredSignature,
  Box14_DateOfCurrent,
  Box15_OtherDate,
  Box16_UnableToWork,
  Box17_ReferringProvider,
  Box18_Hospitalization,
  Box19_AdditionalClaim,
  Box20_OutsideLab,
  Box21_Diagnosis,
  Box22_ResubmissionCode,
  Box23_PriorAuth,
  Box24_ServiceLines,
  Box25_FederalTaxId,
  Box26_PatientAccountNo,
  Box27_AcceptAssignment,
  Box28_TotalCharge,
  Box29_AmountPaid,
  Box30_ReservedNUCC30,
  Box31_PhysicianSignature,
  Box32_ServiceFacility,
  Box33_BillingProvider,
} from './boxes';
import { ClaimAnnotationsPanel } from './ClaimAnnotationsPanel';
import { combineSx, formStyles } from './ClaimCMS1500Form.styles';
import { transformClaimToForm, transformFormToClaim } from './ClaimCMS1500Form.utils';
import { CMS1500Template } from './CMS1500Template';

interface ClaimCMS1500FormProps {
  claim: Claim;
  readOnly?: boolean;
  onAutosaveStateChange?: (
    status: AutosaveState,
    isDirty: boolean,
    lastSavedAt: Date | null,
    onRetry: () => void,
  ) => void;
  onFormMethodsReady?: (methods: UseFormReturn<CMS1500FormData>) => void;
  onFieldChange?: (fieldName: string) => void;
}

export interface CMS1500FormData {
  // Box 1: Insurance Type (Payer)
  insuranceType:
    | 'medicare'
    | 'medicaid'
    | 'tricare'
    | 'champva'
    | 'groupHealth'
    | 'feca'
    | 'other'
    | '';

  // Box 1a: Insured's ID Number
  insuredIdNumber: string;

  // Box 2: Patient's Name
  patientName: {
    lastName: string;
    firstName: string;
    middleInitial: string;
  };

  // Box 3: Patient Birth Date & Sex
  patientBirthDate: string;
  patientSex: '' | 'M' | 'F';

  // Box 4: Insured's Name
  insuredName: {
    lastName: string;
    firstName: string;
    middleInitial: string;
  };

  // Box 5: Patient's Address
  patientAddress: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
  };

  // Box 6: Patient Relationship to Insured
  patientRelationship: 'self' | 'spouse' | 'child' | 'other';

  // Box 7: Insured's Address
  insuredAddress: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
  };

  // Box 8: Reserved for NUCC Use
  reservedNUCC: string;

  // Box 9: Other Insured's Name
  otherInsuredName: {
    lastName: string;
    firstName: string;
    middleInitial: string;
  };

  // Box 9a: Other Insured's Policy or Group Number
  otherInsuredPolicyGroup: string;

  // Box 9b: Reserved for NUCC Use
  otherInsuredReservedNUCC: string;

  // Box 9c: Reserved for NUCC Use
  otherInsuredReservedNUCC2: string;

  // Box 9d: Insurance Plan Name or Program Name
  otherInsuredPlanName: string;

  // Box 10: Is Patient's Condition Related To
  conditionRelated: {
    employment?: boolean;
    autoAccident?: boolean;
    autoAccidentState: string;
    otherAccident?: boolean;
    claimCodes: string;
  };

  // Box 11: Insured's Policy Group or FECA Number
  insuredPolicyGroup: string;

  // Box 11a: Insured's Date of Birth & Sex
  insuredDateOfBirth: string;
  insuredSex: '' | 'M' | 'F';

  // Box 11b: Other Claim ID
  otherClaimId: string;

  // Box 11c: Insurance Plan Name or Program Name
  insuredPlanName: string;

  // Box 11d: Is There Another Health Benefit Plan?
  otherHealthPlan?: boolean;

  // Box 12: Patient's or Authorized Person's Signature
  patientSignature: {
    signed: boolean;
    date: string;
  };

  // Box 13: Insured's or Authorized Person's Signature
  insuredSignature: {
    signed: boolean;
    date: string;
  };

  // Box 14: Date of Current Illness/Injury/Pregnancy
  currentIllnessDate: string;
  currentIllnessQualifier: string;

  // Box 15: Other Date
  otherDate: string;
  otherDateQualifier: string;

  // Box 16: Dates Patient Unable to Work
  unableToWorkFrom: string;
  unableToWorkTo: string;

  // Box 17: Name of Referring Provider
  referringProviderName: string;
  referringProviderIdQualifier: string;
  referringProviderNPI: string;

  // Box 18: Hospitalization Dates
  hospitalizationFrom: string;
  hospitalizationTo: string;

  // Box 19: Additional Claim Information
  additionalClaimInfo: string;

  // Box 20: Outside Lab
  outsideLab: boolean;
  outsideLabCharges: string;

  // Box 21: Diagnosis or Nature of Illness or Injury
  icdIndicator: '9' | '0';
  diagnosisCodes: {
    A: string;
    B: string;
    C: string;
    D: string;
    E: string;
    F: string;
    G: string;
    H: string;
    I: string;
    J: string;
    K: string;
    L: string;
  };

  // Box 22: Resubmission Code & Original Ref. No.
  resubmissionCode: string;
  originalRefNo: string;

  // Box 23: Prior Authorization Number
  priorAuthNumber: string;

  // Box 24: Service Lines (6 lines)
  serviceLines: Array<{
    dateFrom: string;
    dateTo: string;
    placeOfService: string;
    emg: string;
    cptHcpcs: string;
    modifiers: string; // Legacy field for backward compatibility
    modifier1?: string;
    modifier2?: string;
    modifier3?: string;
    modifier4?: string;
    diagnosisPointer: string;
    charges: string;
    units: string;
    epsdt: string;
    idQualifier: string;
    renderingProviderId: string;
  }>;

  // Box 25: Federal Tax I.D. Number
  federalTaxIdNumber: string;
  taxIdType: 'SSN' | 'EIN';

  // Box 26: Patient's Account No.
  patientAccountNo: string;

  // Box 27: Accept Assignment?
  acceptAssignment: boolean;

  // Box 28: Total Charge
  totalCharge: string;

  // Box 29: Amount Paid
  amountPaid: string;

  // Box 30: Reserved for NUCC Use
  reservedNUCC30: string;

  // Box 31: Signature of Physician or Supplier
  physicianSignature: {
    signed: boolean;
    date: string;
    credentials: string;
  };

  // Box 32: Service Facility Location Information
  serviceFacility: {
    name: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
    npi: string;
  };

  // Box 33: Billing Provider Info & Ph #
  billingProvider: {
    name: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    npi: string;
    otherId: string;
  };
}

// Currency formatting utilities
const formatCurrencyForDisplay = (value: string | number): string => {
  const numericValue = String(value).replace(/[^0-9.]/g, '');
  const num = parseFloat(numericValue);
  if (isNaN(num)) return '0.00';
  return num.toFixed(2);
};

const cleanCurrencyInput = (value: string): string => {
  // Remove all non-numeric characters except decimal point
  let cleaned = value.replace(/[^0-9.]/g, '');

  // Handle multiple decimal points - keep only first one
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }

  // Limit to 2 decimal places
  if (parts.length === 2 && parts[1].length > 2) {
    cleaned = parts[0] + '.' + parts[1].substring(0, 2);
  }

  // Prevent leading decimal point
  if (cleaned.startsWith('.')) {
    cleaned = '0' + cleaned;
  }

  // Validate the result is a valid number
  const num = parseFloat(cleaned);
  if (isNaN(num) || num < 0) {
    return '0';
  }

  return cleaned;
};

export const ClaimCMS1500Form: React.FC<ClaimCMS1500FormProps> = ({
  claim,
  readOnly = false,
  onAutosaveStateChange,
  onFormMethodsReady,
  onFieldChange,
}) => {
  const [updateClaim] = useUpdateClaimMutation();

  const defaultValues = useMemo(() => transformClaimToForm(claim), [claim]);

  const methods = useForm<CMS1500FormData>({
    defaultValues,
    disabled: readOnly,
  });

  const { watch, setValue } = methods;

  // Notify parent when form methods are ready
  useEffect(() => {
    onFormMethodsReady?.(methods);
  }, [methods, onFormMethodsReady]);

  // Auto-calculate Box 28 Total Charge from service line charges
  useEffect(() => {
    const calculateTotal = (serviceLines: CMS1500FormData['serviceLines']) => {
      const total = serviceLines.reduce((sum, line) => {
        if (line && line.charges) {
          const charge = parseFloat(line.charges);
          if (!isNaN(charge)) {
            return sum + charge;
          }
        }
        return sum;
      }, 0);
      return total.toFixed(2);
    };

    const currentServiceLines = watch('serviceLines') || [];
    const initialTotal = calculateTotal(currentServiceLines);
    setValue('totalCharge', initialTotal, { shouldDirty: false, shouldValidate: false });

    const subscription = watch((value, { name }) => {
      if (name && name.includes('serviceLines')) {
        const serviceLines = value.serviceLines || [];
        const formattedTotal = calculateTotal(serviceLines as CMS1500FormData['serviceLines']);
        setValue('totalCharge', formattedTotal, { shouldDirty: true, shouldValidate: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);

  // Autosave implementation
  const handleAutosave = useCallback(
    async (data: CMS1500FormData) => {
      const updatedClaim = transformFormToClaim(data, claim);
      await updateClaim({
        claimId: claim.id || '',
        updateClaimRequest: updatedClaim,
      }).unwrap();
    },
    [claim, updateClaim],
  );

  const {
    status: autosaveStatus,
    isDirty: autosaveIsDirty,
    lastSavedAt,
    triggerSave,
    retry,
  } = useAutosave({
    onSave: handleAutosave,
    debounceMs: 800,
  });

  // Watch all form changes and trigger autosave (only when not readOnly)
  useEffect(() => {
    if (readOnly) return;

    const subscription = watch((data, { type, name }) => {
      // Only trigger autosave on actual user input changes (type === 'change')
      // This prevents autosave from triggering when:
      // - Scrubber sets errors via setError/clearErrors
      // - Form is initialized with data
      // - Any programmatic setValue calls are made
      if (type !== 'change') return;

      // Notify parent of field change to clear scrubber errors
      if (name && onFieldChange) {
        onFieldChange(name);
      }

      if (data) {
        triggerSave(data as CMS1500FormData);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, triggerSave, readOnly, onFieldChange]);

  // Notify parent of autosave state changes
  useEffect(() => {
    onAutosaveStateChange?.(autosaveStatus, autosaveIsDirty, lastSavedAt, retry);
  }, [autosaveStatus, autosaveIsDirty, lastSavedAt, retry, onAutosaveStateChange]);

  return (
    <FormProvider {...methods}>
      <form>
        <Paper
          sx={
            readOnly
              ? combineSx(formStyles.formContainer, formStyles.readOnlyForm)
              : formStyles.formContainer
          }
        >
          <CMS1500Template
            // Row 1
            box1={<Box01_InsuranceType />}
            box1a={<Box01a_InsuredId />}
            // Row 2
            box2={<Box02_PatientName />}
            box3={<Box03_PatientBirthSex />}
            box4={<Box04_InsuredName />}
            // Row 3
            box5={<Box05_PatientAddress />}
            box6={<Box06_PatientRelationship />}
            box7={<Box07_InsuredAddress />}
            box8={<Box08_ReservedNUCC />}
            // Row 4
            box9={<Box09_OtherInsured />}
            box10={<Box10_PatientCondition />}
            box11={<Box11_InsuredPolicy />}
            // Row 5
            box12={<Box12_PatientSignature />}
            box13={<Box13_InsuredSignature />}
            // Row 6
            box14={<Box14_DateOfCurrent />}
            box15={<Box15_OtherDate />}
            box16={<Box16_UnableToWork />}
            // Row 7
            box17={<Box17_ReferringProvider />}
            box18={<Box18_Hospitalization />}
            // Row 8
            box19={<Box19_AdditionalClaim />}
            box20={<Box20_OutsideLab />}
            // Row 9
            box21={<Box21_Diagnosis />}
            box22={<Box22_ResubmissionCode />}
            box23={<Box23_PriorAuth />}
            // Row 10 - Service Lines
            box24={<Box24_ServiceLines cleanCurrencyInput={cleanCurrencyInput} />}
            // Row 11 - Totals
            box25={<Box25_FederalTaxId />}
            box26={<Box26_PatientAccountNo />}
            box27={<Box27_AcceptAssignment />}
            box28={<Box28_TotalCharge formatCurrencyForDisplay={formatCurrencyForDisplay} />}
            box29={<Box29_AmountPaid cleanCurrencyInput={cleanCurrencyInput} />}
            box30={<Box30_ReservedNUCC30 />}
            // Row 12 - Provider Info
            box31={<Box31_PhysicianSignature />}
            box32={<Box32_ServiceFacility />}
            box33={<Box33_BillingProvider />}
          />

          {/* Annotations Panel - Mobile only (Desktop shows in sidebar) */}
          <Box sx={{ display: { xs: 'block', lg: 'none' }, mt: 2 }}>
            <AnnotationsPanelErrorBoundary claimId={claim.id || undefined}>
              <ClaimAnnotationsPanel claim={claim} position="bottom" />
            </AnnotationsPanelErrorBoundary>
          </Box>
        </Paper>
      </form>
    </FormProvider>
  );
};
